import React from "react";
import { Button } from "../../../../components/ui/button";

/**
 * Side-by-side comparison of multiple AI-generated variants.
 * Each column shows the key fields produced by GPT-4.1 and a button to apply
 * that variant to the form.
 */
export const AICompareDialog = ({ open, onClose, variants, onApply }) => {
  if (!open) return null;
  const ordered = [...variants].reverse(); // newest first
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="ai-compare-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-[95vw] max-h-[90vh] w-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-emerald-50 to-blue-50">
          <div>
            <h3 className="text-lg font-bold text-gray-800">📊 Confronta varianti AI</h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {ordered.length} version{ordered.length === 1 ? "e" : "i"} generat{ordered.length === 1 ? "a" : "e"}. Clicca "Usa questa" per applicarla al modulo.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
            data-testid="ai-compare-close-btn"
            aria-label="Chiudi confronto"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${ordered.length}, minmax(280px, 1fr))` }}>
            {ordered.map((v, idx) => {
              const isNewest = idx === 0;
              return (
                <div
                  key={v.timestamp}
                  className={`border-2 rounded-lg p-3 flex flex-col gap-2 ${isNewest ? "border-emerald-400 bg-emerald-50/30" : "border-gray-200 bg-white"}`}
                  data-testid={`ai-variant-card-${idx}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-gray-700">{v.toneLabel}</span>
                    {isNewest && (
                      <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">ATTIVA</span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {new Date(v.timestamp).toLocaleTimeString("it-IT")}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Titolo</div>
                    <div className="text-sm font-medium text-gray-900 leading-tight">{v.data.title || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Descrizione IT</div>
                    <div className="text-xs text-gray-700 leading-snug whitespace-pre-wrap max-h-40 overflow-auto bg-white border border-gray-100 rounded p-1.5">
                      {v.data.description_it || "—"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Brand</div>
                      <div className="text-gray-800">{v.data.brand || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Categoria</div>
                      <div className="text-gray-800">{v.data.category || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Peso</div>
                      <div className="text-gray-800">{v.data.weight_estimate_g || "—"} g</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Condizione</div>
                      <div className="text-gray-800">{v.data.condition || "—"}</div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => onApply(v)}
                    size="sm"
                    className="mt-auto bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`ai-variant-apply-${idx}`}
                  >
                    ✓ Usa questa versione
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
